/**
 * Game Preview Page
 * 
 * Phase 6.0 Track E: Preview games with server-authoritative outcomes
 */

import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  Button,
  Banner,
  Spinner,
  Text,
} from "@shopify/polaris";
import { PixiApp } from "../games/runtime/PixiApp";
import { WheelGame } from "../games/wheel_of_fortune/WheelGame";
import { loadWheelTextures } from "../games/wheel_of_fortune/textureLoader";
import { previewGame, type GamePreviewResponse } from "../api/campaigns";
import type { GameState, WheelConfig } from "../games/runtime/types";

function GamePreviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const variantId = searchParams.get("variantId");

  const canvasRef = useRef<HTMLDivElement>(null);
  const pixiAppRef = useRef<PixiApp | null>(null);
  const wheelGameRef = useRef<WheelGame | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [gameState, setGameState] = useState<GameState>("loading");
  const [preview, setPreview] = useState<GamePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  /**
   * Load preview data from backend
   */
  useEffect(() => {
    const loadPreview = async () => {
      if (!variantId) {
        setError("variantId is required");
        setGameState("error");
        return;
      }

      try {
        setGameState("loading");
        const previewData = await previewGame({ variantId });
        setPreview(previewData);
        console.log("[GamePreview] Preview loaded:", previewData);
      } catch (err) {
        console.error("[GamePreview] Failed to load preview:", err);
        setError(err instanceof Error ? err.message : "Failed to load preview");
        setGameState("error");
      }
    };

    loadPreview();
  }, [variantId]);

  /**
   * Initialize Pixi and Wheel Game
   */
  useEffect(() => {
    if (!preview || !canvasRef.current) return;

    const initGame = async () => {
      try {
        // 1. Preload wheel textures
        console.log('[GamePreview] Loading wheel textures...');
        const textures = await loadWheelTextures();
        console.log('[GamePreview] ✅ Textures loaded');
        
        // 2. Create Pixi app
        const pixiApp = new PixiApp();
        await pixiApp.init(canvasRef.current!, {
          width: 600,
          height: 600,
          backgroundColor: 0x1a1a2e,
          antialias: true,
        });
        pixiAppRef.current = pixiApp;

        // 3. Create wheel game (with preloaded textures)
        const wheelConfig = preview.config as unknown as WheelConfig;
        const wheelGame = new WheelGame({
          config: wheelConfig,
          textures, // Preloaded PNG textures
          width: 600,
          height: 600,
          onStateChange: (state) => {
            console.log("[GamePreview] State changed:", state);
            setGameState(state);
            if (state === "resolved") {
              setIsPlaying(false);
            }
          },
        });
        wheelGameRef.current = wheelGame;

        const stage = pixiApp.getStage();
        if (stage) {
          stage.addChild(wheelGame);
        }

        // Start game loop
        const gameLoop = () => {
          if (wheelGame) {
            wheelGame.update(1 / 60); // Assuming 60fps
          }
          animationFrameRef.current = requestAnimationFrame(gameLoop);
        };
        gameLoop();

        setGameState("ready");
        console.log("[GamePreview] Game initialized");
      } catch (error) {
        console.error("[GamePreview] Init error:", error);
        setError(error instanceof Error ? error.message : "Failed to initialize game");
        setGameState("error");
      }
    };

    initGame();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (pixiAppRef.current) {
        pixiAppRef.current.destroy();
      }
    };
  }, [preview]);

  /**
   * Handle play button click
   */
  const handlePlay = () => {
    if (!wheelGameRef.current || !preview || gameState !== "ready") {
      console.warn("[GamePreview] Cannot play in state:", gameState);
      return;
    }

    setIsPlaying(true);
    wheelGameRef.current.spin(preview.outcome);
  };

  /**
   * Handle reset
   */
  const handleReset = () => {
    if (wheelGameRef.current) {
      wheelGameRef.current.reset();
    }
    setIsPlaying(false);
  };

  if (!variantId) {
    return (
      <Page
        title="Game Preview"
        backAction={{ content: "Back", onAction: () => navigate(-1) }}
      >
        <Banner tone="critical">
          <Text as="p">variantId is required</Text>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Game Preview"
      subtitle={`Variant ID: ${variantId}`}
      backAction={{ content: "Back", onAction: () => navigate(-1) }}
    >
      <BlockStack gap="400">
        {gameState === "loading" && (
          <Card>
            <BlockStack gap="200">
              <Spinner size="large" />
              <Text as="p" alignment="center">
                Loading preview...
              </Text>
            </BlockStack>
          </Card>
        )}

        {error && (
          <Banner tone="critical">
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {preview && (
          <>
            <Banner tone="info">
              <Text as="p">
                ✅ Preview loaded from backend. Outcome is server-authoritative.
              </Text>
            </Banner>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {preview.config.segments ? "Wheel of Fortune" : "Game Preview"}
                </Text>

                <div
                  ref={canvasRef}
                  style={{
                    width: "600px",
                    height: "600px",
                    border: "2px solid #e1e3e5",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                />

                <BlockStack gap="200">
                  <Button
                    onClick={handlePlay}
                    disabled={gameState !== "ready" || isPlaying}
                    variant="primary"
                  >
                    {isPlaying ? "Playing..." : "Play Preview"}
                  </Button>

                  <Button
                    onClick={handleReset}
                    disabled={gameState === "loading" || isPlaying}
                  >
                    Reset
                  </Button>

                  <Text as="p" tone="subdued">
                    State: <strong>{gameState}</strong>
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">
                  Preview Details
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Outcome Key:</strong> {preview.outcome.outcomeKey}
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Segment Label:</strong> {preview.outcome.segmentLabel}
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Target Angle:</strong> {preview.outcome.targetAngle.toFixed(2)}°
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Total Rotation:</strong> {preview.outcome.totalRotation.toFixed(2)}°
                </Text>
              </BlockStack>
            </Card>
          </>
        )}
      </BlockStack>
    </Page>
  );
}

export default GamePreviewPage;
